import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Public account-deletion page. THIS URL GOES IN PLAY CONSOLE.
 *
 *   EN  /delete-account
 *   FR  /fr/supprimer-mon-compte
 *
 * D-B — sign-in to delete. There is no email-verified request flow at launch:
 * an unauthenticated endpoint whose effect is destruction is a liability, and a
 * deletion link sitting in an inbox is a real hazard for this user base, where
 * a family dispute over an elderly relative's phone is foreseeable.
 *
 * The page must be readable and complete WITHOUT an account — the Play reviewer
 * has no login. Everything that deletion does or keeps is stated here; only the
 * button requires a session.
 */
export const DeleteAccount: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isFr = i18n.language === 'fr';

  // <head> for this route is injected at build time by scripts/prerender.js
  // (SEO_BY_ROUTE), the same as /about and /contact — not by a runtime
  // component. The Play reviewer loads a prerendered page, so the copy below
  // must stand on its own with JavaScript disabled.
  return (
    <>
      <main className="min-h-screen bg-brand-bg-warm py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Link
            to={isFr ? '/fr' : '/'}
            className="text-sm text-brand-text-muted hover:text-brand-text mb-6 inline-block"
          >
            {t('deleteAccountPage.backHome')}
          </Link>

          <h1 className="text-3xl font-bold text-brand-text mb-3">{t('deleteAccountPage.title')}</h1>
          <p className="text-brand-text-muted mb-8">{t('deleteAccountPage.intro')}</p>

          <section className="bg-white rounded-xl border border-brand-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-brand-text mb-3">{t('deleteAccountPage.how.title')}</h2>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-brand-text">
              <li>{t('deleteAccountPage.how.step1')}</li>
              <li>{t('deleteAccountPage.how.step2')}</li>
              <li>{t('deleteAccountPage.how.step3')}</li>
            </ol>
            <button
              // Login reads `location.state.from.pathname` — not a query
              // param — so send the caregiver back to My Account after sign-in.
              onClick={() => navigate('/login', { state: { from: { pathname: '/account' } } })}
              className="mt-5 w-full sm:w-auto px-6 py-3 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              {t('deleteAccountPage.signInCta')}
            </button>
          </section>

          <section className="bg-white rounded-xl border border-brand-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-brand-text mb-3">{t('deleteAccountPage.deleted.title')}</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-brand-text">
              <li>{t('deleteAccountPage.deleted.device')}</li>
              <li>{t('deleteAccountPage.deleted.contacts')}</li>
              <li>{t('deleteAccountPage.deleted.health')}</li>
              <li>{t('deleteAccountPage.deleted.location')}</li>
              <li>{t('deleteAccountPage.deleted.account')}</li>
            </ul>
            <p className="text-sm text-brand-text-muted mt-4">{t('deleteAccountPage.deleted.timing')}</p>
          </section>

          <section className="bg-white rounded-xl border border-brand-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-brand-text mb-3">{t('deleteAccountPage.kept.title')}</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-brand-text">
              <li>{t('deleteAccountPage.kept.consent')}</li>
              <li>{t('deleteAccountPage.kept.invoices')}</li>
              <li>{t('deleteAccountPage.kept.stopList')}</li>
            </ul>
          </section>

          {/* D-D */}
          <section className="bg-amber-50 rounded-xl border border-amber-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-amber-900 mb-2">{t('deleteAccountPage.billing.title')}</h2>
            <p className="text-sm text-amber-900">{t('deleteAccountPage.billing.noRefund')}</p>
          </section>

          <section className="bg-white rounded-xl border border-brand-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-brand-text mb-3">{t('deleteAccountPage.phone.title')}</h2>
            <p className="text-sm text-brand-text">{t('deleteAccountPage.phone.body')}</p>
          </section>

          <p className="text-sm text-brand-text-muted">
            {t('deleteAccountPage.help')}{' '}
            <a href="mailto:privacy@aidfone.com" className="text-brand-primary underline">
              privacy@aidfone.com
            </a>
          </p>
        </div>
      </main>
    </>
  );
};

export default DeleteAccount;
